import { Select } from "antd";
import React, { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "hooks/redux";
import { getVariablesFromUrl, updateUrlParams } from "state/url_params/utils";
import { trackUserAction } from "utils/trackEvent";

import type { CSSProperties } from "react";

const defaultStyles = {
  minWidth: 120,
};

export interface BaseProps {
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  paramKey: string;
}

interface Props<T, P> extends BaseProps {
  onSelect?: (selectedValue: P) => void;
  data: ReadonlyArray<T>;
  getKey: (t: T) => string | number;
  getTitle?: (t: T) => string;
  getValue: (t: T) => P;
  convertParamVariable?: (value: string) => P;
  additionalFilterPredicates?: Array<(input: string, t: T) => boolean>;
  renderOptionContent?: (t: T) => React.ReactNode;
}

const SelectFilter = <T, P extends string | number>({
  data,
  getKey,
  getValue,
  getTitle,
  onSelect,
  className,
  style,
  placeholder,
  paramKey,
  convertParamVariable,
  additionalFilterPredicates = [],
  renderOptionContent,
}: Props<T, P>) => {
  const dispatch = useDispatch();
  const queryVariables = useSelector(getVariablesFromUrl);

  const selectedValue = useMemo(() => {
    const value = queryVariables[paramKey];
    if (value && convertParamVariable) {
      return convertParamVariable(value) ?? undefined;
    }
    return (value ?? undefined) as P | undefined;
  }, [queryVariables, convertParamVariable, paramKey]);

  const handleChange = useCallback(
    (itemId: P) => {
      onSelect?.(itemId);
      dispatch(updateUrlParams({ [paramKey]: itemId }));
      trackUserAction("select_filter_search", { paramKey: paramKey, selectedItemId: itemId });
    },
    [onSelect, dispatch, paramKey]
  );

  const selectStyle = useMemo(
    () => (style ? { ...defaultStyles, ...style } : defaultStyles),
    [style]
  );

  const getPopupContainer = useCallback(
    (trigger: HTMLElement) => trigger.parentNode as HTMLElement,
    []
  );

  const filterOption = useCallback(
    (input: string, option: any) =>
      option?.title.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
      (option?.item != null &&
        additionalFilterPredicates.some(predicate => predicate(input, option.item))),
    [additionalFilterPredicates]
  );

  const selectOptions = useMemo(
    () =>
      data.map(value => {
        const title = getTitle ? getTitle(value) : (getValue(value) as string);
        return (
          <Select.Option key={getKey(value)} value={getValue(value)} title={title} item={value}>
            {renderOptionContent?.(value) ?? title}
          </Select.Option>
        );
      }),
    [data, getKey, getValue, getTitle, renderOptionContent]
  );

  return (
    <Select
      allowClear
      showSearch
      dropdownMatchSelectWidth={false}
      placeholder={placeholder}
      className={className}
      style={selectStyle}
      value={selectedValue}
      onChange={handleChange}
      getPopupContainer={getPopupContainer}
      filterOption={filterOption}
    >
      {selectOptions}
    </Select>
  );
};

export default SelectFilter;
